/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

const _ = require('underscore');  //TODO: should try to remove this dependency

/**
 * The SummaryCollector is used to save statistics based on one or more values for one or more measures.
 * The table it owns is keyed one per measure item.
 **/
const NO_DETAIL_ROWCOUNT = undefined;

class SummaryCollector {
	/**
	 * @Constructor
	 * @param {Object} WidgetApi
	 */
	constructor(dashboardApi, widgetApi, isSelectionSummaryCollector = false) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.isSelectionSummaryCollector = isSelectionSummaryCollector;
		this.lastCategorySelection = [];
		this.summaryTable = {};
	}

	static get NO_DETAIL_ROWCOUNT() {
		return NO_DETAIL_ROWCOUNT;
	}

	/**
	 * Return a more 'user-friendly' version of the summary table
	 * (keyed on itemId, aggregationType and layerId rather than dataItemUniqueId).
	 * This type of key could be used for crude matching in a compare rules processor.
	 * (ie same item, aggregation and layer can definitely be compared!)
	 * @returns a JSON friendly summaryTable.
	 */
	getSummaryTableJSON() {
		let summaryTableJSON = {};
		this.summaryTable = JSON.parse(JSON.stringify(this.summaryTable));

		_.each(this.summaryTable, (summaryTableEntry, key) => {
			const jsonKey = (summaryTableEntry.metadata)
				? summaryTableEntry.metadata.itemId + '_' + summaryTableEntry.metadata.aggregationType + '_' + summaryTableEntry.metadata.layerId
				: key;
			summaryTableJSON[jsonKey]=summaryTableEntry;

			//Convert valueMap to a values array (use object iteration to preserve selected order).
			if (summaryTableEntry.valueMap) {
				summaryTableEntry.values = [];
				_.each(summaryTableEntry.valueMap, (value) => {
					summaryTableEntry.values.push(value);
				});
			}
		});

		let rtnValue = {
			measures: summaryTableJSON
		};
		if (this.lastCategorySelection.length) {
			//return "formatted value" for null = [no value]
			const Formatter=this.widgetApi.getVisApi() && this.widgetApi.getVisApi().getFormatter();
			if (Formatter) {
				rtnValue.lastCategorySelection = this.lastCategorySelection.map((tuplePart) => {
					return (tuplePart) ? tuplePart : Formatter.format(tuplePart);
				});
			} else {
				rtnValue.lastCategorySelection = this.lastCategorySelection;
			}
		}
		return rtnValue;
	}

	setLastCategorySelection(lastCategorySelection) {
		this.lastCategorySelection=lastCategorySelection;
	}

	/**
	 * Clear out the summaryTable to collect new summaries.
	 * @param layerId (optional) - if specified, only entries for a particular layer will be removed.
	 */
	reset(layerId=null) {
		if (layerId) {
			Object.keys(this.summaryTable).forEach((summaryTableKey) => {
				const summaryTableEntry = this.summaryTable[summaryTableKey];
				if (summaryTableEntry.metadata && summaryTableEntry.metadata.layerId===layerId) {
					this.summaryTable[summaryTableKey]=null;
					delete this.summaryTable[summaryTableKey];
				}
			});
		} else {
			this.summaryTable={};
		}
	}

	/**
	 * get the summary entry for the passed key (or add a new summaryEntry)
	 * @param dataItemUniqueId - the uniqueId of the dataItem is the key of the summaryTable.
	 * @returns the new summary entry.
	 */
	getSummary(dataItemUniqueId) {
		if (!this.summaryTable[dataItemUniqueId]) {
			this._initializeSummary(dataItemUniqueId);
		}
		return this.summaryTable[dataItemUniqueId];
	}

	removeSummary(dataItemUniqueId) {
		if (this.summaryTable[dataItemUniqueId]) {
			this.summaryTable[dataItemUniqueId]=null;
			delete this.summaryTable[dataItemUniqueId];
		}
	}

	/**
	 * incorporate a single value into the summary
	 * @param summaryEntry - the summaryEntry of interest
	 * @param value - the value to incorporate
	 * @param detailRowCountUse (optional) - if available, the detail rowCount associated with this summary value.
	 */
	updateSummary(summaryEntry, value, detailRowCountUse) {
		const updatedSummaryEntry = this.updateSummaryEntry(summaryEntry);
		if (value === null) {
			// Null indicates a missing value.
			updatedSummaryEntry.missingValueCount = (updatedSummaryEntry.missingValueCount === undefined) ? 1 : updatedSummaryEntry.missingValueCount + 1;
			return;
		}
		if (updatedSummaryEntry.maxUse===undefined || updatedSummaryEntry.maxUse < value) {
			updatedSummaryEntry.maxUse = value;
		}
		if (updatedSummaryEntry.minUse===undefined || updatedSummaryEntry.minUse > value) {
			updatedSummaryEntry.minUse = value;
		}
		if (value < 0) {
			updatedSummaryEntry.hasNegativeValues = true;
		}
		updatedSummaryEntry.totalUse = (updatedSummaryEntry.totalUse===undefined) ? value : updatedSummaryEntry.totalUse + value;
		this._updateWeightedAverageFields(updatedSummaryEntry, value, detailRowCountUse);
	}

	/**
	 * copies summaryEntry and replaces it in the table with the clone
	 * we need to do this because when the vis in explore compare card is updated
	 * and retrieve and update entries in summary table, entries are read-only
	 * by copying the object we make the object modifiable
	 * @param summaryEntry - the summaryEntry of interest
	 * @returns summaryEntry
	 */
	updateSummaryEntry(summaryEntry) {
		const itemUniqueId = summaryEntry.metadata && summaryEntry.metadata.itemUniqueId;
		if (itemUniqueId) {
			let newSummaryEntry = Object.assign({}, summaryEntry);
			this.summaryTable[itemUniqueId] = newSummaryEntry;
			return newSummaryEntry;
		} else {
			return summaryEntry;
		}
	}

	/**
	 * This function applies to stacked bar/column and area charts where each entry is Grouped by the first category
	 * into a 'stack'.  Regardless of the aggregation type, each stack is the sum of its positive segments and its negative segments. The domain
	 * of the chart is the min and max of these sums.
	 * @param summaryEntry - the summaryEntry of interest
	 * @param segmentValue - the segment value to add to a stack of segments.
	 * @param stackId - the stackId (which is the same as the entryIndex of the grouping column) eg: 2004 = 0, 2005 = 1
	 */
	updateStackedSummary(summaryEntry, segmentValue, stackId) {
		summaryEntry.stackedSummaryValueMap = summaryEntry.stackedSummaryValueMap || {};
		let { currentPosHeightOfStack, currentNegHeightOfStack } = summaryEntry.stackedSummaryValueMap[stackId] || {};
		if (segmentValue >= 0) {
			currentPosHeightOfStack = (currentPosHeightOfStack === undefined)? segmentValue : currentPosHeightOfStack + segmentValue;
		} else {
			currentNegHeightOfStack = (currentNegHeightOfStack === undefined)? segmentValue : currentNegHeightOfStack + segmentValue;
		}
		summaryEntry.stackedSummaryValueMap[stackId] = {
			currentPosHeightOfStack,
			currentNegHeightOfStack
		};
	}

	/**
	 * once all data has been processed, produce a finalized summary (stackedDomainMax and stackedDomainMin for each stack)
	 * @param summaryEntry - the entry of interest (eg. Revenue)
	 */
	finalizeStackedSummary(summaryEntry) {
		const heightArray = [];
		_.each(summaryEntry.stackedSummaryValueMap, (stackEntry) => {
			if(typeof stackEntry.currentPosHeightOfStack !== 'undefined'){
				heightArray.push(stackEntry.currentPosHeightOfStack);
			}
			if(typeof stackEntry.currentNegHeightOfStack !== 'undefined'){
				heightArray.push(stackEntry.currentNegHeightOfStack);
			}
		});

		summaryEntry.stackedDomainMax = heightArray.length? Math.max(... heightArray): undefined;
		summaryEntry.stackedDomainMin = heightArray.length? Math.min(... heightArray): undefined;
		delete summaryEntry.stackedSummaryValueMap;
	}

	/**
	 * A selection summary stores a map of tuple to value.  This allows insertion or removal of a value when
	 * a tuple is added/removed from the selection.
	 * @param summaryEntry - the summaryEntry of interest
	 * @param rowSummary - the value to incorporate
	 *				.dataItemUniqueId - the item being summarized,
	 *				.value: - the value for the selection being incorporated
	 *				.detailRowCountUse (optional): rowCount });

	 * @param tuple - the selected tuple.
	 * @param ctrlSelect - (optional) if ctrlSelect is true, an existing entry for a given tuple will be deleted.
	 */
	updateSelectionSummary(summaryEntry, rowSummary, tuple, ctrlSelect) {
		if (!summaryEntry.valueMap) {
			summaryEntry.valueMap = {};
		}
		if (ctrlSelect && summaryEntry.valueMap[JSON.stringify(tuple)]) {
			delete summaryEntry.valueMap[JSON.stringify(tuple)];
		} else {
			summaryEntry.valueMap[JSON.stringify(tuple)]=rowSummary.value;
			this._updateWeightedAverageFields(summaryEntry, rowSummary.value, rowSummary.detailRowCountUse);
		}
	}

	/**
	 * complete calculations that only need to be done once per dataset.
	 * @param summaryEntry - the summaryEntry of interest (ie: for a particular measure)
	 * @param rowCount - the rowCount (for computing average)
	 * @param aggregate - if the aggregate is average, we don't return a total.
	 */
	finalizeSummaries(summaryEntry, rowCount = 0, aggregate) {
		this.updateStackedSummary(summaryEntry);
		this.finalizeStackedSummary(summaryEntry);
		const Formatter=this.widgetApi.getVisApi().getFormatter();
		summaryEntry.rowCount = rowCount - (summaryEntry.missingValueCount || 0);
		if (summaryEntry.rowCount > 0) {
			summaryEntry.avgUse = (summaryEntry.totalUse) / summaryEntry.rowCount;
		}
		if (summaryEntry.detailSumUse!==undefined && summaryEntry.detailRowCountUse!==undefined) {
			summaryEntry.weightedAvgUse = summaryEntry.detailSumUse / summaryEntry.detailRowCountUse;
		}
		const formatSpec=summaryEntry.metadata && summaryEntry.metadata.format;
		if (Formatter && formatSpec) {
			summaryEntry.min = summaryEntry.minUse!==undefined && Formatter.format(summaryEntry.minUse, formatSpec);
			summaryEntry.max = summaryEntry.maxUse!==undefined && Formatter.format(summaryEntry.maxUse, formatSpec);
			summaryEntry.total=summaryEntry.totalUse!==undefined && Formatter.format(summaryEntry.totalUse, formatSpec);
			summaryEntry.avg = summaryEntry.avgUse!==undefined && Formatter.format(summaryEntry.avgUse, formatSpec);
			if (summaryEntry.weightedAvgUse!==undefined) {
				summaryEntry.weightedAvg = Formatter.format(summaryEntry.weightedAvgUse, formatSpec);
			}
		} else {
			//If no Formatter is supplied, return unformatted results.
			summaryEntry.min = summaryEntry.minUse;
			summaryEntry.max = summaryEntry.maxUse;
			summaryEntry.total=summaryEntry.totalUse;
			summaryEntry.avg = summaryEntry.avgUse;
			if (summaryEntry.weightedAvgUse!==undefined) {
				summaryEntry.weightedAvg = summaryEntry.weightedAvgUse;
			}
		}

		//Cleanup information that explore shouldn't have.
		if (aggregate==='avg' || aggregate==='countdistinct') {
			//Don't produce totals for averages or countdistinct.
			delete summaryEntry.totalUse;
			delete summaryEntry.total;
		}
		//TODO: We could potentially remove the formatSpec
	}

	/**
	 * Selection helper method that allows a caller to pass selectionInfo to the SummaryCollector
	 * and apply the associated values.
	 * @param selectionInfo - the selectionInfo object which includes the ordinal selections and values.
	 * @param ctrlSelect - a flag that signifies that this is a multiple selection (eg lasso select) and should be treated as a complete selection.
	 * @returns true if selectionInfo can be applied.
	 */
	applySelectionInfo(selectionInfo, ctrlSelect) {
		if (!selectionInfo._aOrdinalSelections || !selectionInfo._aOrdinalSelections.length ) {
			return false;
		}
		let tuple = _.map(selectionInfo._aCategorySelections, (categorySelection) => {
			return { u: categorySelection.value.u, d: categorySelection.value.d };
		});
		_.each(selectionInfo._aOrdinalSelections, (ordinalSelection) => {
			const selEntry = this.getSummary(ordinalSelection.slotDataItem.getUniqueId());
			this.updateSelectionSummary(selEntry, ordinalSelection, tuple, ctrlSelect);
		});

		return true;
	}

	/**
	 * Selection helper method that allows a caller to pass selectionInfo for a lassoSelection to the SummaryCollector
	 * @param selectionInfo - the selectionInfo for the lasso selection (see details below)
	 * @param lassoSelectTupleSize - the number of elements in each point in the lasso selection (eg: 2007:Fax = 2)
	 */
	applyLassoSelectionInfo(selectionInfo, lassoSelectTupleSize) {
		//For lasso selections, there are N values per tuple in _aOrdinalSelections as usual (ie: 1 value for each measure).
		//THEN, each additional selection has the values for each corresponding tuple in _aAdditionalOrdinalSelections.
		//The categorical values are all in _aCategorySelections in a flat array lassoSelectTupleSize long.
		//Eg: ['2007', 'Fax', '2008', 'Mail'] and lassoSelectTupleSize of 2 would represent 2 datapoints 2007:Fax, 2008:Mail.
		//Not the greatest structure but complete and that's what is used for all lasso selects in liveWidget.

		//Step1: make a single array of values.
		const allValues = selectionInfo._aOrdinalSelections.concat(selectionInfo._aAdditionalOrdinalSelections);

		//Step2: remove extra information from the categorical part tuple (ie: the parent info to form the proper keys)
		let allTuples = _.map(selectionInfo._aCategorySelections, (categorySelection) => {
			return { u: categorySelection.value.u, d: categorySelection.value.d };
		});

		let tuple = null;
		_.each(allValues, (thisValue, idx) => {
			//whenever we have read all N measures, fetch the next tuple.
			if (idx % (selectionInfo._aOrdinalSelections.length) ===  0) {
				tuple = _.first(allTuples, lassoSelectTupleSize);
				allTuples = _.rest(allTuples, lassoSelectTupleSize);
			}
			const selEntry = this.getSummary(thisValue.slotDataItem.getUniqueId());
			this.updateSelectionSummary(selEntry, thisValue, tuple);
		});
	}

	/**
	 * @returns true if any of the entries in the summaryTable are missing summaryValues (unfiltered measure values)
	 */
	areOverallSummaryValuesNeeded() {
		return _.find(this.summaryTable, (summaryTableEntry) => {
			return summaryTableEntry.summaryValue===undefined;
		}) ? true : false;
	}

	_addMetadata(dataItemUniqueId) {
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const foundMappingInfo = content.getFeature('Visualization').getSlots().getMappingInfo(dataItemUniqueId);

		if (foundMappingInfo) {
			const dataItemAPI=foundMappingInfo.dataItem;
			const slotAPI=foundMappingInfo.slot;
			const aggregationType=dataItemAPI.getAggregation();
			const datasetIdList = slotAPI ? slotAPI.getDefinition().getDatasetIdList() : [];
			const layerId = datasetIdList[0];
			return {
				itemUniqueId: dataItemAPI.getId(),
				itemId: dataItemAPI.getColumnId(),
				label: dataItemAPI.getLabel(),
				aggregationType: aggregationType,
				layerId: layerId,
				format: dataItemAPI.getFormat(),
				slotId: slotAPI.getId()
			};
		}
		return null;
	}

	//If this summary entry includes a detail row count, save it, a sum and the weighted average into the summaryEntry.
	_updateWeightedAverageFields(summaryEntry, aggregatedValue, detailRowCountUse) {
		if (detailRowCountUse !== NO_DETAIL_ROWCOUNT) {
			const detailSumUse = aggregatedValue * detailRowCountUse;
			summaryEntry.detailSumUse = (summaryEntry.detailSumUse===undefined) ? detailSumUse : summaryEntry.detailSumUse + detailSumUse;
			summaryEntry.detailRowCountUse = (summaryEntry.detailRowCountUse===undefined) ? detailRowCountUse : summaryEntry.detailRowCountUse + detailRowCountUse;
			summaryEntry.weightedAvgUse = summaryEntry.detailSumUse / summaryEntry.detailRowCountUse;
		}
	}

	/**
	 * add a new summaryItemEntry to the summaryTable.
	 * @param itemKey - the key (unique to this measure)
	 * @param dataItemUniqueId - the uniqueId of the dataItem.
	 * //TODO: this key should be collapsed into one...the dataItemUniqueId OR the itemId, aggregation type and layerId.
	 */
	_initializeSummary(dataItemUniqueId) {
		this.summaryTable[dataItemUniqueId]={
			metadata: this._addMetadata(dataItemUniqueId)
		};
	}

	isTupleInSelectionSummary(tuple) {
		let lastTupleKey;

		if (Array.isArray(tuple)) {
			lastTupleKey = JSON.stringify(tuple);
		} else {
			lastTupleKey = JSON.stringify([tuple]);
		}

		const isInSelectionSummary = Object.keys(this.summaryTable).some(summaryTableKey => {
			if (this.summaryTable[summaryTableKey].valueMap[lastTupleKey] !== undefined) {
				return true;
			}
		});
		return isInSelectionSummary;
	}
}

export default SummaryCollector;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/SummaryCollector.js